import * as React from "react";
import { Link } from "gatsby";
import { StaticImage } from "gatsby-plugin-image";
import { Helmet } from "react-helmet";
import Layout from "../components/Layout/Layout";

const serviceGroups = [
  {
    title: "Support for everyday life",
    text: "Care for the problems that can make normal days feel heavy, confusing, or hard to manage.",
    services: [
      "Anxiety",
      "Depression",
      "Grief and Loss",
      "Life Transitions",
      "PTSD",
    ],
  },
  {
    title: "Children, parents, and families",
    text: "Therapy for children, caregivers, and family systems working through change or conflict.",
    services: [
      "Play Therapy",
      "Parenting",
      "ADD/ADHD",
      "Blended Family Issues",
      "Divorce",
      "Custody",
      "Parent Coordination",
    ],
  },
  {
    title: "Trauma, identity, and relationships",
    text: "A compassionate place to work through painful experiences, identity questions, and relational stress.",
    services: ["Trauma and Abuse", "Family of Origin", "Adoption"],
  },
  {
    title: "Gender identity and sexual orientation",
    text: "Affirming support for clients exploring identity and orientation.",
    services: ["Gender Identity", "Sexual Orientation"],
  },
  {
    title: "Specialized clinical needs",
    text: "Experienced therapists with training across complex emotional, behavioral, and life-stage concerns.",
    services: [],
  },
];

const therapists = [
  {
    name: "Michelle VanDenHul",
    license: "MSW, CSW-PIP",
    specialty: "Children, trauma, play therapy",
    to: "/therapists/michelle-vandenhul",
    photo: (
      <StaticImage
        src="./therapists/michelle-vandenhul/michelle-vandenhul-2.jpg"
        alt="Michelle VanDenHul"
        layout="constrained"
        width={420}
        className="h-64 w-full rounded-3xl shadow-lg"
        imgStyle={{ objectPosition: "center 25%" }}
      />
    ),
  },
  {
    name: "Erin Nielsen Ogdahl",
    license: "JD, CSW-PIP, LAC",
    specialty: "Substance use, family issues, custody evaluations",
    to: "/therapists/erin-nielsen-ogdahl",
    photo: (
      <StaticImage
        src="./therapists/erin-nielsen-ogdahl/erin-nielsen-ogdahl.jpg"
        alt="Erin Nielsen Ogdahl"
        layout="constrained"
        width={420}
        className="h-64 w-full rounded-3xl shadow-lg"
      />
    ),
  },
  {
    name: "Jennifer Gleason-Wilson",
    license: "MA, LPC",
    specialty:
      "ADHD support, anxiety relief, depression management, goal-oriented growth, leadership development, life transitions",
    to: "/therapists/jennifer-gleason-wilson",
    photo: (
      <StaticImage
        src="./therapists/jennifer-gleason-wilson/jennifer-gleason-wilson-2.jpeg"
        alt="Jennifer Gleason-Wilson"
        layout="constrained"
        width={420}
        className="h-64 w-full rounded-3xl shadow-lg"
        imgStyle={{ objectPosition: "center 10%" }}
      />
    ),
  },
  {
    name: "Christine Ellis",
    license: "MSW, CSW-PIP",
    specialty: "Families, adolescents, trauma, grief",
    to: "/therapists/christine-ellis",
    photo: (
      <StaticImage
        src="./therapists/christine-ellis/christine-ellisv2.jpg"
        alt="Christine Ellis"
        layout="constrained"
        width={420}
        className="h-64 w-full rounded-3xl shadow-lg"
      />
    ),
  },
  {
    name: "Melissa Steever",
    license: "MA, LPC-MH",
    specialty: "Adolescents, anxiety, relationships, trauma",
    to: "/therapists/melissa-steever",
    photo: (
      <StaticImage
        src="./therapists/melissa-steever/melissa.jpeg"
        alt="Melissa Steever"
        layout="constrained"
        width={420}
        className="h-64 w-full rounded-3xl shadow-lg"
      />
    ),
  },
  {
    name: "Jeanne Bunkers",
    license: "MSW, CSW-PIP",
    specialty: "Children, adolescents, adults, life adjustments",
    to: "/therapists/jeanne-bunkers",
    photo: (
      <StaticImage
        src="./therapists/jeanne-bunkers/jeanne.jpeg"
        alt="Jeanne Bunkers"
        layout="constrained"
        width={420}
        className="h-64 w-full rounded-3xl shadow-lg"
        imgStyle={{ objectPosition: "center 8%" }}
      />
    ),
  },
];

const IndexPage = () => {
  return (
    <Layout full={true} removeTopSpace={true} className="bg-yellow-50">
      <Helmet title="Integrative Wellness" defer={false} />

      <main className="overflow-hidden bg-yellow-50 text-gray-900">
        <section className="relative bg-yellow-50">
          <div className="absolute inset-x-0 top-0 h-96 bg-gradient-to-b from-green-100 via-green-100 to-yellow-50" />
          <div className="relative mx-auto grid max-w-6xl gap-10 px-6 py-16 lg:grid-cols-2 lg:px-8 lg:py-24">
            <div className="flex flex-col justify-center">
              <p className="mb-5 inline-flex w-max rounded-full bg-white px-5 py-2 text-sm font-bold uppercase tracking-wide text-green-900 shadow">
                Professional Therapy in Sioux Falls, SD
              </p>
              <h1 className="font-serif text-5xl font-bold leading-tight text-gray-900 md:text-7xl">
                A brighter place to begin feeling like yourself again.
              </h1>
              <p className="mt-6 max-w-xl text-xl leading-relaxed text-gray-700">
                We are a group of mental health professionals who serve everyday
                people with everyday problems, with care that feels welcoming,
                grounded, and personal.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a
                  href="tel:1-605-271-1348"
                  className="rounded-full bg-green-900 px-7 py-4 text-center text-base font-bold text-white shadow-lg hover:bg-green-800"
                >
                  Call 605.271.1348
                </a>
                <Link
                  to="/contact"
                  className="rounded-full bg-white px-7 py-4 text-center text-base font-bold text-green-900 shadow-lg hover:bg-green-100"
                >
                  Contact Us
                </Link>
              </div>
            </div>

            <div className="relative min-h-full">
              <div className="absolute -right-10 top-10 h-40 w-40 rounded-full bg-green-300 opacity-60" />
              <div className="absolute -left-8 bottom-14 h-32 w-32 rounded-full bg-yellow-200 opacity-90" />
              <div className="relative grid grid-cols-5 gap-4">
                <div className="col-span-5 overflow-hidden rounded-3xl bg-white p-3 shadow-2xl">
                  <StaticImage
                    src="../images/IMG_5122.JPEG"
                    alt="Bright seating area at Integrative Wellness"
                    layout="constrained"
                    width={900}
                    className="h-96 w-full rounded-2xl"
                  />
                </div>
                <div className="col-span-3 col-start-2 -mt-20 overflow-hidden rounded-3xl bg-white p-3 shadow-2xl md:col-span-2 md:col-start-1">
                  <StaticImage
                    src="../images/IMG_1997.jpeg"
                    alt="Warm therapy office detail at Integrative Wellness"
                    layout="constrained"
                    width={420}
                    className="h-56 w-full rounded-2xl"
                  />
                </div>
                <div className="col-span-5 rounded-3xl bg-white p-6 shadow-xl md:col-span-3 md:-mt-12">
                  <p className="text-sm font-bold uppercase tracking-wide text-green-900 pt-4">
                    Come as you are
                  </p>
                  <p className="mt-2 text-lg leading-relaxed text-gray-700">
                    A comfortable office, practical support, and a team ready to
                    help you find the next right step.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white py-14">
          <div className="mx-auto grid max-w-6xl gap-5 px-6 text-center md:grid-cols-3 lg:px-8">
            <div className="rounded-3xl bg-yellow-50 p-8 shadow">
              <p className="font-serif text-5xl font-bold text-green-900">6</p>
              <p className="mt-2 text-sm font-bold uppercase tracking-wide text-gray-600">
                Therapists
              </p>
            </div>
            <div className="rounded-3xl bg-green-100 p-8 shadow">
              <p className="font-serif text-5xl font-bold text-green-900">
                15+
              </p>
              <p className="mt-2 text-sm font-bold uppercase tracking-wide text-gray-600">
                Areas of Care
              </p>
            </div>
            <div className="rounded-3xl bg-yellow-50 p-8 shadow">
              <p className="font-serif text-5xl font-bold text-green-900">
                Sioux Falls
              </p>
              <p className="mt-2 text-sm font-bold uppercase tracking-wide text-gray-600">
                Local Office
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24">
          <div className="mx-auto max-w-6xl px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-sm font-bold uppercase tracking-wide text-green-900">
                Therapy Services
              </p>
              <h2 className="mt-3 font-serif text-4xl font-bold leading-tight md:text-6xl">
                Professional therapy for the concerns real life brings.
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-gray-700">
                Our therapists support children, adolescents, adults, parents,
                and families through a broad range of mental health needs.
              </p>
            </div>

            <div className="mt-10 grid gap-6 md:grid-cols-2">
              {serviceGroups.map((group) => (
                <article
                  key={group.title}
                  className="rounded-3xl bg-white p-8 shadow-lg"
                >
                  <h3 className="font-serif text-3xl font-bold text-gray-900">
                    {group.title}
                  </h3>
                  <p className="mt-3 leading-relaxed text-gray-700">
                    {group.text}
                  </p>
                  <ul className="mt-6 flex flex-wrap gap-3">
                    {group.services.map((service) => (
                      <li
                        key={service}
                        className="rounded-full bg-green-100 px-4 py-2 text-sm font-semibold text-green-900"
                      >
                        {service}
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-green-100 py-16 md:py-24">
          <div className="mx-auto max-w-6xl px-6 lg:px-8">
            <div className="grid gap-10 lg:grid-cols-2">
              <div>
                <p className="text-sm font-bold uppercase tracking-wide text-green-900">
                  Our Team
                </p>
                <h2 className="mt-3 font-serif text-4xl font-bold leading-tight md:text-6xl">
                  Meet therapists who bring warmth, training, and perspective.
                </h2>
              </div>
              <div className="flex items-end">
                <p className="text-lg leading-relaxed text-gray-700">
                  Each Integrative Wellness therapist has their own areas of
                  focus. Review the team to find a provider whose experience
                  fits what you need.
                </p>
              </div>
            </div>

            <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {therapists.map((therapist) => (
                <article
                  key={therapist.name}
                  className="overflow-hidden rounded-3xl bg-white p-4 shadow-lg"
                >
                  {therapist.photo}
                  <div className="p-4">
                    <h3 className="font-serif text-2xl font-bold text-gray-900">
                      {therapist.name}
                    </h3>
                    <p className="mt-1 font-semibold text-green-900">
                      {therapist.license}
                    </p>
                    <p className="mt-3 leading-relaxed text-gray-700">
                      {therapist.specialty}
                    </p>
                    <Link
                      to={therapist.to}
                      className="mt-5 inline-flex font-bold text-green-900 hover:text-green-700"
                    >
                      Learn more about {therapist.name}
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white py-16 md:py-24">
          <div className="mx-auto grid max-w-6xl gap-10 px-6 lg:grid-cols-2 lg:px-8">
            <div className="overflow-hidden rounded-3xl bg-yellow-50 p-3 shadow-2xl">
              <StaticImage
                src="../images/IMG_8584.jpeg"
                alt="Comfortable therapy office at Integrative Wellness"
                layout="constrained"
                width={900}
                className="h-full min-h-full w-full rounded-2xl"
              />
            </div>
            <div className="flex flex-col justify-center">
              <p className="text-sm font-bold uppercase tracking-wide text-green-900">
                Take a Look Inside
              </p>
              <h2 className="mt-3 font-serif text-4xl font-bold leading-tight md:text-6xl">
                A welcoming space for honest conversations.
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-gray-700">
                The office is designed to feel calm and approachable, with
                comfortable seating, warm lighting, and room to talk through
                what brought you here.
              </p>
              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                <div className="rounded-3xl bg-yellow-50 p-6 shadow">
                  <h3 className="text-xl font-bold text-gray-900">Welcoming</h3>
                  <p className="mt-2 text-gray-700">
                    A casual office environment meant to help you feel at ease.
                  </p>
                </div>
                <div className="rounded-3xl bg-green-100 p-6 shadow">
                  <h3 className="text-xl font-bold text-gray-900">Practical</h3>
                  <p className="mt-2 text-gray-700">
                    Clear next steps for scheduling, payment, and finding care.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-yellow-50 py-16 md:py-24">
          <div className="mx-auto max-w-6xl px-6 lg:px-8">
            <div className="grid gap-10 lg:grid-cols-2">
              <div className="flex flex-col justify-center">
                <p className="text-sm font-bold uppercase tracking-wide text-green-900">
                  Find Us
                </p>
                <h2 className="mt-3 font-serif text-4xl font-bold leading-tight md:text-6xl">
                  Conveniently located in Sioux Falls.
                </h2>
                <p className="mt-5 text-lg leading-relaxed text-gray-700">
                  5010 S Broadband Ln, Ste 100
                  <br />
                  Sioux Falls, SD 57108
                </p>
                <a
                  href="https://www.google.com/maps/dir//5010+S+Broadband+Ln,+Sioux+Falls,+SD+57108"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-8 inline-flex w-max rounded-full bg-green-900 px-7 py-4 text-base font-bold text-white shadow-lg hover:bg-green-800"
                >
                  Get Directions
                </a>
              </div>
              <div className="overflow-hidden rounded-3xl bg-white p-3 shadow-2xl">
                <iframe
                  title="Map to Integrative Wellness"
                  src="https://maps.google.com/maps?q=5010+S+Broadband+Ln+Ste+100,+Sioux+Falls,+SD+57108&output=embed"
                  className="h-96 w-full rounded-2xl"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="px-6 py-16 lg:px-8">
          <div className="mx-auto max-w-6xl overflow-hidden rounded-3xl bg-green-900 shadow-2xl">
            <div className="grid gap-8 p-8 text-white md:grid-cols-3 md:p-12">
              <div className="md:col-span-2">
                <p className="text-sm font-bold uppercase tracking-wide text-green-100">
                  Ready to get started?
                </p>
                <h2 className="mt-3 font-serif text-4xl font-bold md:text-5xl">
                  Call today to schedule an appointment.
                </h2>
                <p className="mt-4 max-w-2xl text-lg leading-relaxed text-green-50">
                  We accept Medicaid, Medicare, most insurances, and private
                  pay. Contact us and we will help you take the next step.
                </p>
              </div>
              <div className="flex flex-col justify-center gap-3">
                <a
                  href="tel:1-605-271-1348"
                  className="rounded-full bg-white px-7 py-4 text-center font-bold text-green-900 shadow-lg hover:bg-green-100"
                >
                  605.271.1348
                </a>
                <Link
                  to="/contact"
                  className="rounded-full bg-green-100 px-7 py-4 text-center font-bold text-green-900 shadow-lg hover:bg-white"
                >
                  Send a Message
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </Layout>
  );
};

export default IndexPage;
